import { GitBranchIcon, useIsLargerThanIpadResolution } from "@octopusdeploy/design-system-components";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import type { BranchSpecifier, GetFavouriteProjectsForUserBffResponse, LicenseStatusResource, ProjectGroupResource, ProjectIntentsResource, ProjectResource, ValidateGitRefV2Response } from "@octopusdeploy/octopus-server-client";
import { HostingEnvironment, Permission, UseDefaultBranch, ValidateGitRefV2ResponseType } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { Environment } from "@octopusdeploy/utilities";
import type { ReactNode } from "react";
import * as React from "react";
import { ProjectLinksVNext } from "~/areas/projects/components/ProjectLayout/ProjectLinksVNext";
import { ResilientPipelineCallouts } from "~/areas/projects/components/ProjectLayout/ResilientPipelineCallouts";
import SampleProjectTour from "~/areas/projects/components/ProjectLayout/SampleProjectTour/SampleProjectTour";
import SampleProjectTourResume from "~/areas/projects/components/ProjectLayout/SampleProjectTour/SampleProjectTourResume";
import { alwaysHideOnboardingQuestionnaireKey } from "~/areas/projects/components/Projects/OnboardingQuestionnaire/AlwaysHideOnboardingQuestionnaire";
import { NewlyCreatedProjectWizardDialog } from "~/areas/projects/components/Projects/OnboardingQuestionnaire/NewlyCreatedProjectWizardDialog";
import { useIsPageVersionControlled } from "~/areas/projects/context/useIsPageVersionControlled";
import { repository } from "~/clientInstance";
import Chip from "~/components/Chips/Chip";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import ErrorContextProvider from "~/components/ErrorContext/ErrorContext";
import ErrorPanel from "~/components/ErrorPanel/ErrorPanel";
import { GettingStartedFooter } from "~/components/GettingStarted/GettingStartedFooter";
import { useSetInPageNavVisible } from "~/components/GlobalLayout/InPageNavVisibilityContext";
import { PortalPageHeaderPrimary } from "~/components/PageHeaderPrimary/PageHeaderPrimary";
import { hasPermission, isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { PortalPageLayout } from "~/components/PortalPageLayout/PortalPageLayout";
import { useIsVerticalNavigationEnabled } from "~/components/RootRoutes/useIsVerticalNavigationEnabled";
import { SkeletonLoadingLayout } from "~/components/SkeletonLoadingLayout/SkeletonLoadingLayout";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { ProjectContextProvider } from "../../context";
import { ProjectStatus } from "../ProjectStatus/ProjectStatus";
import { WelcomeToProjectDialog } from "../Projects/WelcomeToProjectDialog";
import { VcsErrorPage } from "../VersionControl/VcsErrorPage";
import { OptimisticProjectFavouriteIconButton } from "./ProjectFavouriteIconButton";
export interface ProjectLayoutProps {
    spaceId: string;
    projectSlug: string;
    branchName: string | undefined;
    isNewlyCreatedProject?: string | null;
    configureProject?: boolean | null;
    defaultActionType?: string | null | undefined;
    children: (params: {
        project: ProjectResource;
        setShowK8sStatusItem: SetRequiresKubernetesTarget;
    }) => ReactNode;
}
export function ProjectLayout({ spaceId, projectSlug, isNewlyCreatedProject, configureProject, branchName, children, defaultActionType }: ProjectLayoutProps) {
    const isPageVersionControlled = useIsPageVersionControlled();
    const isVerticalNavigationEnabled = useIsVerticalNavigationEnabled();
    const isKubernetesGuidedSetupFeatureEnabled = useOctopusFeatureToggle("kubernetes-guided-setup", false);
    return (<ErrorContextProvider>
            <ProjectLayoutInternal spaceId={spaceId} projectSlug={projectSlug} branchName={branchName} isNewlyCreatedProject={isNewlyCreatedProject} configureProject={configureProject} isPageVersionControlled={isPageVersionControlled} isVerticalNavigationEnabled={isVerticalNavigationEnabled} isKubernetesGuidedSetupEnabled={isKubernetesGuidedSetupFeatureEnabled} defaultActionType={defaultActionType}>
                {children}
            </ProjectLayoutInternal>
        </ErrorContextProvider>);
}
type ProjectLayoutInternalProps = {
    spaceId: string;
    projectSlug: string;
    branchName: string | undefined;
    isNewlyCreatedProject: string | null | undefined;
    configureProject: boolean | null | undefined;
    isPageVersionControlled: boolean;
    isVerticalNavigationEnabled: boolean;
    isKubernetesGuidedSetupEnabled: boolean;
    defaultActionType?: string | null | undefined;
    children: (params: {
        project: ProjectResource;
        setShowK8sStatusItem: SetRequiresKubernetesTarget;
    }) => ReactNode;
};
interface ProjectLayoutState extends DataBaseComponentState {
    projectGroups: ProjectGroupResource[] | null;
    licenseStatus: LicenseStatusResource | null;
    hasEnvironments: boolean | null;
    newlyCreatedDialogOpen: boolean;
    configureProject: boolean;
    isFavourite: boolean | null;
    projectIntents: ProjectIntentsResource | null;
    defaultActionType: string | null | undefined;
    showTargetStatusItemBeforeProcess: boolean;
}
export type SetRequiresKubernetesTarget = (showTargetStatusItemBeforeProcess: boolean) => void;
class ProjectLayoutInternal extends DataBaseComponent<ProjectLayoutInternalProps, ProjectLayoutState> {
    constructor(props: ProjectLayoutInternalProps) {
        super(props);
        this.state = {
            projectGroups: null,
            licenseStatus: null,
            hasEnvironments: null,
            newlyCreatedDialogOpen: props.isNewlyCreatedProject === "true",
            configureProject: !!props.configureProject,
            isFavourite: null,
            projectIntents: null,
            defaultActionType: props.defaultActionType,
            showTargetStatusItemBeforeProcess: false,
        };
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            const projectGroups = repository.ProjectGroups.all();
            const licenseStatus = repository.Licenses.getCurrentStatus();
            const hasEnvironments = repository.Environments.list({ take: 0 }).then((response) => response.TotalResults !== 0);
            const favouriteProjects = hasPermission(Permission.ProjectView) ? repository.Users.getProjectFavourites() : Promise.resolve<GetFavouriteProjectsForUserBffResponse>({ Projects: [] });
            const projectIntents = await repository.Projects.getProjectIntents(this.props.projectSlug, this.props.spaceId);
            this.setState({
                projectGroups: await projectGroups,
                licenseStatus: await licenseStatus,
                hasEnvironments: await hasEnvironments,
                isFavourite: (await favouriteProjects).Projects.some((p) => p.Slug === this.props.projectSlug),
                projectIntents: await projectIntents,
            });
        });
    }
    toBranchSpecifier(branch: string | undefined): BranchSpecifier {
        return branch ? branch : UseDefaultBranch;
    }
    render() {
        const projectSlug = this.props.projectSlug;
        const branchName = this.props.branchName;
        const branchSpecifier = this.toBranchSpecifier(branchName);
        const setShowK8sStatusItem: SetRequiresKubernetesTarget = (showTargetStatusItemBeforeProcess: boolean) => {
            this.setState({ showTargetStatusItemBeforeProcess });
        };
        return (<ProjectContextProvider doBusyTask={this.doBusyTask} projectIdOrSlug={projectSlug} gitRef={branchSpecifier}>
                {({ state }) => {
                const projectLogo = state.model && state.model.Links.Logo;
                if (!state.model) {
                    return (<main>
                                <HideAreaNavPanelWorkaround />
                                <SkeletonLoadingLayout errors={this.errors}/>
                            </main>);
                }
                const navigationSidebarContent = state.model.IsVersionControlled && this.props.isPageVersionControlled && state.gitRefValidationError ? this.getGitErrorPanel(state.gitRefValidationError) : this.props.children({ project: state.model, setShowK8sStatusItem });
                const projectSettingsURL = links.projectSettingsPage.generateUrl({ spaceId: this.props.spaceId, projectSlug: this.props.projectSlug });
                return (<main>
                            <SampleProjectTour project={state.model}>
                                {!this.props.isKubernetesGuidedSetupEnabled ? this.renderNewlyCreatedProjectDialog() : this.renderWelcomeToProjectDialog()}
                                {this.renderErrors()}
                                <PortalPageLayout navItems={ProjectLinksVNext(branchSpecifier, state.model, state.gitRefValidationError?.Type === ValidateGitRefV2ResponseType.ConnectionFailed, state.projectExtensionSettingsMetadata)} header={<PortalPageHeaderPrimary breadcrumbs={this.headerBreadcrumbs(state.model)} logoLinkURL={projectSettingsURL} logo={{ href: projectLogo, accessibleName: "Project Logo" }} title={state.model.Name} statusSection={<ProjectStatus title="Project status" spaceId={this.props.spaceId} doBusyTask={this.doBusyTask} projectSlug={this.props.projectSlug} showTargetStatusItemBeforeProcess={this.state.showTargetStatusItemBeforeProcess}/>} titleIcon={<ProjectPageHeaderTitleIcon projectId={state.model.Id} projectName={state.model.Name} isDisabled={state.model.IsDisabled} isFavourite={this.state.isFavourite} onIsFavouriteChanged={this.onIsFavouriteChanged} isVersionControlled={state.model.IsVersionControlled}/>}/>} callout={<ResilientPipelineCallouts />} tourProgress={<SampleProjectTourResume />} content={navigationSidebarContent} description={state.model.Description} enablePageLayoutVNext={true}/>
                                <GettingStartedFooter />
                            </SampleProjectTour>
                        </main>);
            }}
            </ProjectContextProvider>);
    }
    private getGitErrorPanel(error: ValidateGitRefV2Response) {
        return <VcsErrorPage error={error} projectSlug={this.props.projectSlug} spaceId={this.props.spaceId}/>;
    }
    renderErrors() {
        const errors = this.state && this.errors;
        if (!errors) {
            return null;
        }
        return <ErrorPanel message={errors.message} errors={errors.errors} parsedHelpLinks={errors.parsedHelpLinks} helpText={errors.helpText} helpLink={errors.helpLink}/>;
    }
    private renderNewlyCreatedProjectDialog() {
        return (<NewlyCreatedProjectWizardDialog open={this.state.newlyCreatedDialogOpen || this.state.configureProject} close={() => this.setState({ newlyCreatedDialogOpen: false, configureProject: false })} showOnboardingQuestionnaire={!this.state.configureProject && this.shouldShowOnboardingQuestionnaire()} showCreateEnvironments={this.shouldShowCreateEnvironments()} doBusyTask={this.doBusyTask} spaceId={this.props.spaceId}/>);
    }
    private renderWelcomeToProjectDialog() {
        return (<WelcomeToProjectDialog isOpen={this.state.newlyCreatedDialogOpen} isVersionControlled={!!this.state.projectIntents?.IsVcsProject} selectedStep={this.state.defaultActionType} close={() => this.setState({ newlyCreatedDialogOpen: false })}/>);
    }
    private headerBreadcrumbs(project: ProjectResource) {
        const foundProjectGroup = this.state.projectGroups && this.state.projectGroups.find((pg) => pg.Id === project.ProjectGroupId);
        if (foundProjectGroup) {
            return [
                { label: "Projects", pageUrl: links.projectsPage.generateUrl({ spaceId: project.SpaceId }) },
                { label: foundProjectGroup.Name, pageUrl: links.projectsPage.generateUrl({ spaceId: project.SpaceId }, this.props.isVerticalNavigationEnabled ? { searchValue: foundProjectGroup.Name } : { projectGroupId: project.ProjectGroupId }) },
            ];
        }
        return [{ label: "Projects", pageUrl: links.projectsPage.generateUrl({ spaceId: project.SpaceId }) }];
    }
    private shouldShowOnboardingQuestionnaire() {
        const canShowQuestionnaire = Environment.isInDevelopmentMode() || (this.state.licenseStatus !== null && this.state.licenseStatus.HostingEnvironment !== HostingEnvironment.SelfHosted);
        if (!canShowQuestionnaire) {
            return false;
        }
        const alwaysHideQuestionnaire = localStorage.getItem(alwaysHideOnboardingQuestionnaireKey);
        return alwaysHideQuestionnaire !== "true" && this.props.isNewlyCreatedProject === "true";
    }
    private shouldShowCreateEnvironments() {
        const hasEnvironmentCreatePermission = isAllowed({ permission: Permission.EnvironmentCreate });
        return hasEnvironmentCreatePermission && !this.state.hasEnvironments;
    }
    private onIsFavouriteChanged = (projectId: string, isFavourite: boolean) => {
        return this.doBusyTask(async () => {
            if (isFavourite) {
                await repository.Users.createProjectFavourite(projectId);
            }
            else {
                await repository.Users.deleteProjectFavourite(projectId);
            }
            this.setState((state) => ({ ...state, isFavourite }));
        });
    };
    static displayName = "ProjectLayoutInternal";
}
interface ProjectPageHeaderTitleIconProps {
    projectId: string;
    projectName: string;
    isDisabled: boolean;
    isVersionControlled: boolean;
    isFavourite: boolean | null;
    onIsFavouriteChanged: (projectId: string, isFavourite: boolean) => Promise<boolean>;
}
function ProjectPageHeaderTitleIcon({ projectId, projectName, isDisabled, isVersionControlled, isFavourite, onIsFavouriteChanged }: ProjectPageHeaderTitleIconProps) {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    const setIsFavourite = (isFavourite: boolean) => onIsFavouriteChanged(projectId, isFavourite);
    const isVerticalNavigationEnabled = useIsVerticalNavigationEnabled();
    return (<>
            {isDisabled && (<Chip backgroundColor={themeTokens.color.background.tertiary} labelColor={themeTokens.color.text.secondary} noMargin noTooltip>
                    Disabled
                </Chip>)}
            {isVerticalNavigationEnabled && isFavourite !== null && <OptimisticProjectFavouriteIconButton isFavourite={isFavourite} setIsFavourite={setIsFavourite} projectName={projectName}/>}
            {isVersionControlled && isLargerThanIpad ? <GitBranchIcon emphasis="subtle"/> : undefined}
        </>);
}
/*
Currently the routes we have for branch vs non branch based pages co-exist in the same menu.
This means that the `ProjectLayout` currently gets unmounted and remounted between these
routes. This is especially noticeable for the vertical navigation as we show the area nav
panel when the in page menu is not visible. This is exactly what happens when we navigate
between a git based page vs a non git based page when vertical navigation is enabled.
This workaround provides us with a cheap way to make this transition less jarring until
we can find a way to properly refactor the layout to either be mounted higher up in the
tree OR we consolidate routes in some way (possibly using URL query parameters instead).
 */
function HideAreaNavPanelWorkaround() {
    const setVisible = useSetInPageNavVisible();
    React.useEffect(() => {
        setVisible?.(true);
        return () => setVisible?.(false);
    }, [setVisible]);
    return null;
}
